import { useQueryClient } from '@tanstack/react-query';
import { Pencil01, Plus, Trash01 } from '@untitledui/icons';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useOrg } from '@/contexts/OrgContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { ConfirmationDialog } from '@/lib/base/overlays';
import { Table, TableCard } from '@/lib/base/table';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';
import { QueryErrorState } from '@/lib/components';
import { getUserFriendlyEventError } from '@/lib/crm/core/errors';
import { createEventBuilder } from '@/lib/crm/core/EventBuilder';
import { createEventsClient } from '@/lib/crm/core/EventsClient';
import { fetchFreshSeq } from '@/lib/crm/core/utils';
import { useSlideoutStack } from '@/lib/crm/hooks/useSlideoutStack';
import type { Team } from '@/lib/crm/hooks/useTeams';
import { useTeams } from '@/lib/crm/hooks/useTeams';
import { logger } from '@/lib/logger';
import { useViewHealthMarker } from '@/lib/view-health';

import { TEAMS_ADMIN_PERMISSION } from '../settings-permissions';
import { TeamFormSlideout } from './components/TeamFormSlideout';

// =============================================================================
// TeamRow
// =============================================================================

interface TeamRowProps {
  team: Team;
  onEdit: (team: Team) => void;
  onDelete: (team: Team) => void;
  onOpenRecord: (entityId: string) => void;
}

function TeamRow({ team, onEdit, onDelete, onOpenRecord }: TeamRowProps) {
  const handleEdit = useCallback(() => onEdit(team), [onEdit, team]);
  const handleDelete = useCallback(() => onDelete(team), [onDelete, team]);
  const handleOpenRecord = useCallback(
    () => onOpenRecord(team.entity_id),
    [onOpenRecord, team.entity_id]
  );

  const memberLabel =
    team.member_count === 1 ? '1 member' : `${team.member_count} members`;

  return (
    <Table.Row id={team.entity_id}>
      <Table.Cell>
        <button
          type='button'
          className='cursor-pointer text-sm font-medium text-fg-primary hover:underline'
          onClick={handleOpenRecord}
        >
          {team.display_name}
        </button>
      </Table.Cell>
      <Table.Cell>
        <span className='text-sm text-fg-tertiary'>
          {team.description || '—'}
        </span>
      </Table.Cell>
      <Table.Cell>
        <Badge type='pill-color' size='md' color='gray'>
          {memberLabel}
        </Badge>
      </Table.Cell>
      <Table.Cell align='right'>
        <div className='flex items-center justify-end gap-1'>
          <Tooltip title='Edit team'>
            <TooltipTrigger
              aria-label='Edit team'
              className='cursor-pointer rounded-md p-1.5 text-fg-quaternary transition-colors hover:bg-bg-tertiary hover:text-fg-secondary focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2'
              onPress={handleEdit}
            >
              <Pencil01 className='h-4 w-4' />
            </TooltipTrigger>
          </Tooltip>
          <Tooltip title='Delete team'>
            <TooltipTrigger
              aria-label='Delete team'
              className='cursor-pointer rounded-md p-1.5 text-fg-quaternary transition-colors hover:bg-error-secondary hover:text-error-primary focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2'
              onPress={handleDelete}
            >
              <Trash01 className='h-4 w-4' />
            </TooltipTrigger>
          </Tooltip>
        </div>
      </Table.Cell>
    </Table.Row>
  );
}

// =============================================================================
// TeamsSection
// =============================================================================

export function TeamsSection() {
  useViewHealthMarker('settings-section-ready');
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  const { hasPermissionGroup } = usePermissions();
  const isPermissionsAdmin = hasPermissionGroup(TEAMS_ADMIN_PERMISSION);
  const { user } = useAuthInfo();
  const queryClient = useQueryClient();
  const { openSlideout } = useSlideoutStack();

  const { teams, isLoading, isError, error, refetch } = useTeams(orgId);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [deletingTeam, setDeletingTeam] = useState<Team | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleOpenRecord = useCallback(
    (entityId: string) => {
      openSlideout(entityId);
    },
    [openSlideout]
  );

  const handleOpenCreate = useCallback(() => {
    setEditingTeam(null);
    setDeletingTeam(null);
    setIsCreateOpen(true);
  }, []);

  const handleOpenEdit = useCallback((team: Team) => {
    setEditingTeam(team);
  }, []);

  const handleOpenDelete = useCallback((team: Team) => {
    setDeletingTeam(team);
  }, []);

  const handleSaved = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['teams'],
      exact: false,
    });
  }, [queryClient]);

  const handleCreateOpenChange = useCallback(
    (open: boolean) => {
      setIsCreateOpen(open);
      if (!open) {
        queryClient.invalidateQueries({
          queryKey: ['teams'],
          exact: false,
        });
      }
    },
    [queryClient]
  );

  const handleEditOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setEditingTeam(null);
        queryClient.invalidateQueries({
          queryKey: ['teams'],
          exact: false,
        });
      }
    },
    [queryClient]
  );

  const handleClearDeleting = useCallback(() => {
    setDeletingTeam(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deletingTeam?.entity_id || !orgId || !user) {
      return;
    }

    setIsDeleting(true);

    try {
      const freshSeq = await fetchFreshSeq(
        orgId,
        'ahoy_team',
        deletingTeam.entity_id
      );

      const builder = createEventBuilder({
        sourceType: 'ahoy_user',
        sourceId: String(user.id),
        startSeq: freshSeq,
      });

      builder.objectDeleted(deletingTeam.entity_id, 'ahoy_team');

      await createEventsClient(orgId).submitEvents(builder.build());

      await queryClient.invalidateQueries({
        queryKey: ['teams'],
        exact: false,
      });

      toast.success('Team deleted');
      setDeletingTeam(null);
    } catch (err) {
      toast.error(getUserFriendlyEventError(err));
      logger.error(
        'Failed to delete team',
        err instanceof Error ? err : new Error(String(err)),
        { org_id: orgId, entity_id: deletingTeam.entity_id }
      );
    } finally {
      setIsDeleting(false);
    }
  }, [deletingTeam, orgId, user, queryClient]);

  if (!isPermissionsAdmin) {
    return (
      <div className='flex flex-col items-center justify-center py-12 text-center'>
        <p className='text-sm text-fg-tertiary'>
          You don&apos;t have access to manage teams.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className='mb-4 flex items-center justify-between'>
        <div>
          <h2 className='text-lg font-semibold text-fg-primary'>Teams</h2>
          <p className='mt-0.5 text-sm text-fg-tertiary'>
            Create and manage teams to group org members together.
          </p>
        </div>
        <Button
          size='sm'
          color='secondary'
          iconLeading={Plus}
          onClick={handleOpenCreate}
        >
          Create team
        </Button>
      </div>

      <TableCard.Root>
        <TableCard.ScrollArea>
          <Table aria-label='Teams' size='sm'>
            <Table.Header>
              <Table.Head label='Name' isRowHeader />
              <Table.Head label='Description' />
              <Table.Head label='Members' />
              <Table.Head label='Actions' align='right' />
            </Table.Header>
            <Table.Body>
              {isLoading && (
                <Table.Row id='loading'>
                  <Table.Cell colSpan={4}>
                    <span className='text-sm text-fg-tertiary'>
                      Loading teams…
                    </span>
                  </Table.Cell>
                </Table.Row>
              )}
              {isError && !isLoading && (
                <Table.Row id='error'>
                  <Table.Cell colSpan={4}>
                    <QueryErrorState
                      error={error}
                      label="We couldn't load your teams"
                      onRetry={refetch}
                      size='sm'
                    />
                  </Table.Cell>
                </Table.Row>
              )}
              {!isLoading && !isError && teams.length === 0 && (
                <Table.Row id='empty'>
                  <Table.Cell colSpan={4}>
                    <div className='flex flex-col items-center justify-center gap-3 py-8'>
                      <div className='text-center'>
                        <p className='text-sm font-medium text-fg-primary'>
                          No teams yet
                        </p>
                        <p className='mt-0.5 text-sm text-fg-tertiary'>
                          Create a team to group members together.
                        </p>
                      </div>
                      <Button
                        size='sm'
                        color='primary'
                        iconLeading={Plus}
                        onClick={handleOpenCreate}
                      >
                        Create team
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              )}
              {!isLoading &&
                !isError &&
                teams.map(team => (
                  <TeamRow
                    key={team.entity_id}
                    team={team}
                    onOpenRecord={handleOpenRecord}
                    onEdit={handleOpenEdit}
                    onDelete={handleOpenDelete}
                  />
                ))}
            </Table.Body>
          </Table>
        </TableCard.ScrollArea>
      </TableCard.Root>

      {orgId && (
        <>
          <TeamFormSlideout
            isOpen={isCreateOpen}
            onOpenChange={handleCreateOpenChange}
            orgId={orgId}
            editingTeam={null}
            onSaved={handleSaved}
          />

          <TeamFormSlideout
            isOpen={!!editingTeam}
            onOpenChange={handleEditOpenChange}
            orgId={orgId}
            editingTeam={editingTeam}
            onSaved={handleSaved}
          />
        </>
      )}

      {deletingTeam && (
        <ConfirmationDialog
          isOpen={!!deletingTeam}
          title='Delete team?'
          description='Members will be removed from this team. This cannot be undone.'
          variant='destructive'
          confirmLabel='Delete'
          isLoading={isDeleting}
          onConfirm={handleConfirmDelete}
          onCancel={handleClearDeleting}
        />
      )}
    </>
  );
}
